import React, { useState, useEffect } from 'react';
import { FiEdit, FiSave, FiX } from 'react-icons/fi';

const TermsConditions = ({ 
  leadId, 
  baseURL,
  onUpdateSuccess 
}) => {
  const [termsConditions, setTermsConditions] = useState('');
  const [editText, setEditText] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTermsConditions = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await fetch(`${baseURL}/api/leads/${leadId}/terms-conditions`);
        
        if (!response.ok) {
          throw new Error('Failed to fetch terms and conditions');
        }
        
        const data = await response.json();
        
        if (data.success) {
          setTermsConditions(data.data.terms_conditions);
        } else {
          setError(data.message);
        }
      } catch (err) {
        console.error('Error fetching terms conditions:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (leadId) {
      fetchTermsConditions();
    }
  }, [leadId, baseURL]);

  const handleEdit = () => {
    setIsEditing(true);
    setEditText(termsConditions);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditText(termsConditions);
    setError(null);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      
      const response = await fetch(`${baseURL}/api/leads/${leadId}/terms-conditions`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          terms_conditions: editText
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to update terms and conditions');
      }
      
      if (data.success) {
        setTermsConditions(data.data.terms_conditions);
        setIsEditing(false);
        if (onUpdateSuccess) {
          onUpdateSuccess(data.data.terms_conditions);
        }
        alert('Terms & conditions updated successfully!');
      } else {
        throw new Error(data.message);
      }
    } catch (err) {
      console.error('Error updating terms conditions:', err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const formatTermsConditions = (text) => {
    if (!text) return 'No terms and conditions specified.';
    
    const lines = text.split('\n').filter(line => line.trim());
    
    return lines.map((line, index) => (
      <div key={index} className="terms-statement">
        <span className="terms-number">{index + 1}.</span>
        <span className="terms-text">{line.trim()}</span>
      </div>
    ));
  };

  return (
<>
      <div className="adminviewproducts-viewlead-d-flex adminviewproducts-viewlead-justify-content-between adminviewproducts-viewlead-align-items-center adminviewproducts-viewlead-mb-3">
        <h5 className="adminviewproducts-viewlead-mb-0">Terms & Conditions</h5>
        {!isEditing && (
          <button
            className="adminviewproducts-viewlead-btn adminviewproducts-viewlead-btn-outline-primary adminviewproducts-viewlead-btn-sm"
            onClick={handleEdit}
            disabled={loading}
          >
            <FiEdit className="adminviewproducts-viewlead-me-1" />
            Edit
          </button>
        )}
        {isEditing && (
          <div>
            <button
              className="adminviewproducts-viewlead-btn adminviewproducts-viewlead-btn-success adminviewproducts-viewlead-btn-sm adminviewproducts-viewlead-me-2"
              onClick={handleSave}
              disabled={saving}
            >
              <FiSave className="adminviewproducts-viewlead-me-1" />
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button
              className="adminviewproducts-viewlead-btn adminviewproducts-viewlead-btn-secondary adminviewproducts-viewlead-btn-sm"
              onClick={handleCancel}
              disabled={saving}
            >
              <FiX className="adminviewproducts-viewlead-me-1" />
              Cancel
            </button>
          </div>
        )}
      </div>

      {error && (
        <div className="adminviewproducts-viewlead-alert adminviewproducts-viewlead-alert-danger adminviewproducts-viewlead-mb-3">
          {error}
        </div>
      )}

      {loading ? (
        <div className="adminviewproducts-viewlead-text-center">
          <p>Loading terms and conditions...</p>
        </div>
      ) : isEditing ? (
        <div className="adminviewproducts-viewlead-card adminviewproducts-viewlead-card-body adminviewproducts-viewlead-bg-light">
          <textarea
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            className="adminviewproducts-viewlead-form-control"
            rows="10"
            placeholder="Enter terms and conditions here..."
            style={{
              fontSize: '14px',
              lineHeight: '1.5',
              fontFamily: 'inherit'
            }}
          />
          <small className="adminviewproducts-viewlead-text-muted">
            Use new lines for separate statements. Each line will be numbered automatically.
          </small>
        </div>
      ) : (
        <div 
          className="adminviewproducts-viewlead-card adminviewproducts-viewlead-card-body adminviewproducts-viewlead-bg-light"
          style={{ 
            fontSize: '14px',
            lineHeight: '1.5',
            maxHeight: '400px',
            overflowY: 'auto',
            padding: '15px'
          }}
        >
          {formatTermsConditions(termsConditions)}
        </div>
      )}
   </>
  );
};

export default TermsConditions;